import React from 'react';
import { isChunkLoadError, reloadOnceForNewBuild } from '@/lib/chunkRecovery';

// Wraps the lazy routes (#618). A stale chunk after a deploy would otherwise
// leave a blank page. Chunk errors reload once, then show a message; any other
// error is re-thrown so it is not swallowed.
export default class ChunkErrorBoundary extends React.Component {
  state = { error: null, updating: false };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    if (!isChunkLoadError(error)) return;
    console.error('[chunk-recovery] lazy chunk failed to load:', error?.message);
    if (reloadOnceForNewBuild()) this.setState({ updating: true });
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null, updating: false });
    }
  }

  render() {
    const { error, updating } = this.state;
    if (!error) return this.props.children;
    if (!isChunkLoadError(error)) throw error;

    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6 py-24" role="alert" aria-live="assertive">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-bold text-[#422680] mb-3">
            {updating ? 'Updating…' : 'This page needs a refresh'}
          </h1>
          <p className="text-gray-700 leading-relaxed mb-6">
            {updating
              ? 'A new version of the site is available. Reloading now.'
              : 'We have released an update since you opened this page, so part of it could not load. Reload the page to get the latest version.'}
          </p>
          {!updating && (
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex items-center justify-center bg-gradient-to-r from-[#422680] via-[#660F56] to-[#F54952] text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all"
              >
                Reload page
              </button>
              <a href="/" className="inline-flex items-center justify-center px-6 py-3 rounded-lg font-semibold text-[#422680] border border-[#422680] hover:bg-gray-50">
                Go to the home page
              </a>
            </div>
          )}
        </div>
      </div>
    );
  }
}
